import { io, type Socket } from 'socket.io-client';

/**
 * Singleton socket.io client for cadencez realtime events.
 *
 * Connects to the `/realtime` namespace on the same origin as the
 * REST API (so nginx → backend works without extra routing — the
 * existing vhost is enough). Auth is via the already-present
 * `cadencez_session` HttpOnly cookie — `withCredentials` instructs the
 * browser to include it on the WS handshake.
 *
 * Lazy-connects on first `.on()` and survives across React renders.
 * Components subscribe via `useSocketEvent(...)` (handles cleanup).
 */
const BASE_URL = ((): string => {
  // Prod: absolute origin from VITE_API_URL (e.g. https://api.cadencez.com)
  // Dev:  empty string → socket.io will connect to the page's origin
  return ((import.meta.env.VITE_API_URL as string | undefined) || '');
})();

class SocketService {
  private socket: Socket | null = null;

  private ensure(): Socket {
    if (this.socket) return this.socket;
    // Path: connect to the /realtime namespace.
    // Pass `${BASE}/realtime` as the io target — socket.io-client
    // splits the URL into "host" + "namespace" automatically.
    this.socket = io(`${BASE_URL}/realtime`, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1500,
    });
    return this.socket;
  }

  on<T = unknown>(event: string, handler: (payload: T) => void): void {
    this.ensure().on(event, handler as (...args: unknown[]) => void);
  }
  off<T = unknown>(event: string, handler: (payload: T) => void): void {
    this.socket?.off(event, handler as (...args: unknown[]) => void);
  }
  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
  }
}

export const socketService = new SocketService();
